/* eslint-disable max-len */
import { ok, err } from 'neverthrow'
import { v4 as uuid } from 'uuid'
import { JsonRpcError, MalformedJsonRpcResponseError } from './errors'
import { validateJsonRpcResponse } from './validators/validateJsonRpcResponse'
import { RpcRequest } from './interfaces'
import { AsyncSender } from '../../core/interfaces/sender/AsyncSender'
import { makeHttpRequest } from '../../core/makeHttpRequest/makeHttpRequest'
import { JsonRpcApiError } from './types/JsonRpcApiError'
import { JsonRpcApiResponse } from './types/JsonRpcApiResponse'
import { Config } from './types/Config'
import { Command } from './types/Command'

/**
 * Handles communication with a remote JsonRpc APi in a type safe manner
 */
export class JsonRpcCommandSender implements AsyncSender<Command, unknown, JsonRpcApiError> {
    public readonly config: Config

    constructor(config: Config) {
      this.config = config
    }

    /**
     * Sends the jsonrpc command to the remote API and returns a JsonRpcApiResponse which is
     * of type Result<T, JsonRpcApiError>, where:
     *
     * T is the success jsonRpc 'response' property,
     * JsonRpcApiError is any error happening
     *
     *
     * @param cmd
     */
    public async send<T = unknown>(cmd: Command, id?: string): JsonRpcApiResponse<T> {
      const authToken = await this.config.getAuthToken()

      // The config needed to make a http request
      const httpMethod: 'post' = 'post' // <- Must by typed to string literal to satisfy the type constraints
      const requestConfig = {
        url: this.config.url,
        headers: this.getAuthHeaders(authToken),
        timeout: this.config.timeOut,
        method: httpMethod,
      }

      // Format the specific rpc part ('data' property of the http request)
      const jsonRpcVersion: '2.0' = '2.0' // <- Must by typed to string literal to satisfy the type constraints
      const rpc: RpcRequest<unknown, never> = {
        method: cmd.commandName,
        params: cmd.params,
        id: id || uuid(),
        jsonrpc: jsonRpcVersion,
      }

      const request = {
        ...requestConfig, data: rpc,
      }
      const httpResponse = await makeHttpRequest(request)

      // If it is an error in the network/http layer then return as error
      if (httpResponse.isErr()) {
        return err(httpResponse.error)
      }

      // Check that the response is well formed according to the jsonrpc 2.0 spec
      const validResp = validateJsonRpcResponse(httpResponse.value, rpc)
      if (validResp.isErr()) {
        const error = new MalformedJsonRpcResponseError({
          message: validResp.error,
          httpRequest: request,
          rpcResponse: httpResponse.value,
        })
        return err(error)
      }

      // At this point we know it is a valid json rpc response according to the spec,
      // Now determine if it is a jsonRpc error response or a jsonRpc success response
      const rpcResponse = validResp.value

      // If the jsonRpc response has an 'error' property
      // it is a jsonRpc error response according to jsonRpc spec.
      if (rpcResponse.error) {
        const error = new JsonRpcError({
          code: rpcResponse.error.code,
          message: rpcResponse.error.message,
          httpRequest: request,
          rpcResponse,
        })
        return err(error)
      }

      // If we get here, we know it must have a 'result' property
      // indicating it is a success response according to jsonRpc spec.
      const result = rpcResponse.result as T
      return ok(result)
    }

    private getAuthHeaders(authToken: string | null) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      }
    }
}
